import {
  MovieFilterSharp as MovieFilterSharpIcon,
  PhotoCameraSharp as PhotoCameraSharpIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import { LoadingButton } from '@mui/lab';
import {
  Avatar,
  Box,
  Card,
  Chip,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tabs,
  tabsClasses,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { StyledComponent } from 'src/components/styled-components/styledComponent';
import { useRightSidebar } from 'src/layouts/right-side-bar/rightSidebarProvider';
import HttpServices from 'src/services/httpService';
import { postsModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import DynamicHeightComponent from 'src/utils/dynamicSize.component';
import Spinner from 'src/utils/Loader/spinner';
import { PostDetailsHelper } from 'src/utils/postDetailsHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';
import Scrollbar from '../../../components/scrollbar';
import { UserListHead } from '../../../sections/@dashboard/user';
import SnapchatMobileViewPosts from '../mobile-publishedPosts/snapchatMobileViewPosts';
import NoPostFound from '../noPost.component';
import SnapchatPostDetailView from './snapchatPostDetailView';

const SnapchatPostGrid = ({ connectedNetworks, handleProfileClick, isShowOfferBanner = false }) => {
  const { t: translate } = useTranslation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showToast } = useToaster();
  const { showRightSidebar } = useRightSidebar();
  const navigate = useNavigate();

  const hasIntegratationPermission = CommonHelper.CheckForPermissionAvailableForUser(
    CommonHelper.PermissionList.INTEGRATE_PERMISSION
  );

  const [isLoading, setLoading] = useState(true);
  const [mobileLoader, setMobileLoader] = useState(false);
  const [snapchatId, setSnapchatId] = useState('');
  const [publishedPosts, setPublishedPosts] = useState([]);
  const [connectedMedias, setConnectedMedias] = useState([]);
  const [height, setHeight] = useState(window.innerHeight - 250);
  const [pagination, setPagination] = useState('');
  const [isAccountNeedsReauth, setAccountNeedsReauth] = useState(false);
  const [viewType, setViewType] = useState('public_story');

  // Table Variables
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const TABLE_HEAD = isMobile
    ? [
        { id: 'created_time', label: translate('published_post.date_time'), alignRight: false },
        { id: 'image_url', label: translate('published_post.image'), alignRight: false },
        { id: 'title', width: '20%', label: translate('published_post.title'), alignRight: false },
      ]
    : [
        { id: 'created_time', width: '30%', label: translate('published_post.date_time'), alignRight: false },
        { id: 'image_url', width: '30%', label: translate('published_post.image'), alignRight: false },
        { id: 'title', width: '20%', label: translate('snapchat_published_post.title'), alignRight: false },
        { id: 'status', width: '20%', label: translate('snapchat_published_post.status'), alignRight: false },
      ];

  useEffect(() => {
    const activeNetworks = connectedNetworks.filter((c) => c.is_active);
    const firstId = activeNetworks?.[0]?.id;

    if (firstId) setSnapchatId(firstId);
    setConnectedMedias(connectedNetworks);

    if (activeNetworks.length === 0) {
      setLoading(false);
      setAccountNeedsReauth(true);
    }

    const handleResize = () => setHeight(window.innerHeight - 300);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [connectedNetworks]);

  useEffect(() => {
    if (snapchatId) getSnapchatPublishedPosts();
  }, [snapchatId,viewType]);

  const getSnapchatPublishedPosts = (limit = 10) => {
    setLoading(true);
    let baseUrl;
          switch (viewType) {
            case 'spotlight':
              baseUrl = postsModuleURL.snapchatSportlights;
              break;
            case 'saved_story':
              baseUrl = postsModuleURL.snapchatSavedStories;
              break;
            default:
              baseUrl = postsModuleURL.snapchatPosts;
              break;
          }
    let getProps = {
      url: `${baseUrl}${snapchatId}&post_limit=${limit}`,
      successCallback: (data) => {
        setPublishedPosts(data.posts || []);
        setPagination(data.pagination || '');
        setLoading(false);
      },
      failureCallback: (msg) => {
        setPublishedPosts([]);
        msg && showToast(msg, 'e');
        setLoading(false);
      },
    };
    HttpServices.Get(getProps);
  };

  const getNextSnapchatPublishedPosts = () => {
    if (!pagination) return;
    setMobileLoader(true);
     let baseUrl;
          switch (viewType) {
            case 'spotlight':
              baseUrl = postsModuleURL.snapchatSportlights;
              break;
            case 'saved_story':
              baseUrl = postsModuleURL.snapchatSavedStories;
              break;
            default:
              baseUrl = postsModuleURL.snapchatPosts;
              break;
          }
    let getProps = {
      url: `${baseUrl}${snapchatId}&pagination=${pagination}&post_limit=${rowsPerPage}`,
      successCallback: (data) => {
        setPublishedPosts((prev) => [...prev, ...(data.posts || [])]);
        setPagination(data.pagination || '');
        setMobileLoader(false);
      },
      failureCallback: (msg) => {
        setMobileLoader(false);
        msg && showToast(msg, 'e');
      },
    };
    HttpServices.Get(getProps);
  };
  const getNextMobileSnapchatPublishedPosts = () => {
    if (!pagination) return;
    setMobileLoader(true);
     let baseUrl;
          switch (viewType) {
            case 'spotlight':
              baseUrl = postsModuleURL.snapchatSportlights;
              break;
            case 'saved_story':
              baseUrl = postsModuleURL.snapchatSavedStories;
              break;
            default:
              baseUrl = postsModuleURL.snapchatPosts;
              break;
          }
    let getProps = {
      url: `${baseUrl}${snapchatId}&pagination=${pagination}&post_limit=10`,
      successCallback: (data) => {
        setPublishedPosts((prev) => [...prev, ...(data.posts || [])]);
        setPagination(data.pagination || '');
        setMobileLoader(false);
      },
      failureCallback: (msg) => {
        setMobileLoader(false);
        msg && showToast(msg, 'e');
      },
    };
    HttpServices.Get(getProps);
  };

  const handleSnapchat = (id) => {
    setSnapchatId(id);
    setPage(0);
    setRowsPerPage(10);
  };

  const handleOpenPost = (post) => {
    const data = {
      content: postView,
      data: post,
    };
    showRightSidebar(data);
  };
  const handleViewType = (e, value) => {
    setViewType(value);
    setPage(0);
    setRowsPerPage(10);
    setPublishedPosts([]); // Clear existing posts when switching types
  };
  const postView = (post) => {
    const profile = connectedMedias.find((a) => a.id === snapchatId);
    return <SnapchatPostDetailView post={post} viewType={viewType} profile={profile} height={height} />;
  };

  const getTableBodyData = () => {
    return publishedPosts.length > 0 ? (
      publishedPosts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
        <StyledComponent.StyledTableRow key={row.id} onClick={() => handleOpenPost(row)} hover>
          <StyledComponent.StyledTableCell align="left">
            {CommonHelper.getTableDateAndTime(row.created_time)}
          </StyledComponent.StyledTableCell>
          <StyledComponent.StyledTableCell align="left">
            {PostDetailsHelper.getPostMedias(row.image_url || row.thumbnail_url)}
          </StyledComponent.StyledTableCell>
          <StyledComponent.StyledTableCell align="left">
            {row.title}
          </StyledComponent.StyledTableCell>
          {!isMobile && (
            <StyledComponent.StyledTableCell align="left">
             {row.status || 'LIVE'}
            </StyledComponent.StyledTableCell>
          )}
        </StyledComponent.StyledTableRow>
      ))
    ) : (
      <NoPostFound />
    );
  };
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if ((newPage + 1) * rowsPerPage > publishedPosts.length) {
      getNextSnapchatPublishedPosts();
    }
  };
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    if (newRowsPerPage > publishedPosts?.length) {
      getSnapchatPublishedPosts(newRowsPerPage);
    }
  };
  const getNetworkChip = () => {
    return connectedMedias.map((c) => {
      const isActive = c.id === snapchatId;
      const isProfileActive = c.is_active;
      const showReauth = !isProfileActive && hasIntegratationPermission;

      return (
        <Chip
          key={c.id}
          style={{
            borderRadius: 8,
            ...(isActive && { backgroundColor: theme.palette.primary.main, color: 'white', fontWeight: 800 }),
            ...(!isProfileActive && {
              backgroundColor: theme.palette.error.lighter,
              border: `1px solid ${theme.palette.error.main}`,
            }),
          }}
          sx={{ m: 0.4 }}
          onClick={isProfileActive ? () => handleSnapchat(c.id) : showReauth ? () => handleProfileClick(c) : undefined}
          deleteIcon={showReauth ? <WarningIcon /> : undefined}
          onDelete={showReauth ? () => handleProfileClick(c) : undefined}
          avatar={
            <Avatar
              src={c.profile_image}
              alt={c.profile_name}
              sx={{ height: 40, width: 40, bgcolor: theme.palette.primary.main }}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {c.profile_name || c.id}
              {!isProfileActive && (
                <Typography variant="caption" sx={{ ml: 1, color: theme.palette.error.dark }}>
                  (Needs Re-auth)
                </Typography>
              )}
            </Box>
          }
        />
      );
    });
  };

  return (
    <Box>
      <Box sx={{ minHeight: 'calc(100vh - 150px)', maxHeight: 'calc(100vh - 150px)' }}>
        {connectedMedias?.length > 0 ? (
          <>
            <Card sx={{ mb: 2, p: 2, maxHeight: 110, overflowY: 'auto' }}>
              {getNetworkChip()}
            </Card>
<Stack display="flex-end" sx={{ pb: 2, pt: 0, pl: 0, pr: 0 }}>
              <Tabs
                value={viewType}
                onChange={handleViewType}
                sx={{
                  minHeight: 36, // Reduced from default 48
                  [`& .${tabsClasses.scrollButtons}`]: {
                    '&.Mui-disabled': { display: 'none' },
                  },
                  marginLeft: 'auto',
                  '& .MuiTab-root': {
                    minHeight: 36, // Reduced tab height
                    padding: '6px 12px', // Smaller padding
                  },
                }}
                allowScrollButtonsMobile
              >
                <Tab
                  value="public_story"
                  sx={{
                    fontSize: '0.75rem', // Smaller font
                    padding: '6px 8px', // Compact padding
                    '& svg': {
                      fontSize: '16px', // Icon size
                    },
                  }}
                  label={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5, // Reduced gap
                        fontSize: 'inherit',
                      }}
                    >
                      <InsertPhotoIcon fontSize="inherit" />
                      <span>{translate('snapchat_published_post.public_story')}</span>
                    </Box>
                  }
                />
                <Tab
                  value="spotlight"
                  sx={{
                    fontSize: '0.75rem',
                    padding: '6px 8px',
                    '& svg': {
                      fontSize: '16px',
                    },
                  }}
                  label={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontSize: 'inherit',
                      }}
                    >
                      <MovieFilterSharpIcon fontSize="inherit" />
                      <span>{translate('snapchat_published_post.spotlight')}</span>
                    </Box>
                  }
                />
                <Tab
                  value="saved_story"
                  sx={{
                    fontSize: '0.75rem',
                    padding: '6px 8px',
                    '& svg': {
                      fontSize: '16px',
                    },
                  }}
                  label={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontSize: 'inherit',
                      }}
                    >
                      <PhotoCameraSharpIcon fontSize="inherit" />
                      <span>{translate('snapchat_published_post.saved_story')}</span>
                    </Box>
                  }
                />
              </Tabs>
            </Stack>
            <Card sx={{ boxShadow: isMobile && publishedPosts?.length > 0 && 'none', backgroundColor: isMobile && publishedPosts?.length > 0 && 'transparent' }}>
              {isLoading ? (
                <Spinner height={height} />
              ) : publishedPosts?.length === 0 ? (
                <NoPostFound
                  height={isMobile ? 'calc(100vh - 330px)' : isShowOfferBanner ? 'calc(100vh - 380px)' : 'calc(100vh - 330px)'}
                  contentText={
                    isAccountNeedsReauth
                      ? `${translate('published_post.need_re_authentication')}`
                      : `${translate('published_post.no_post_available')}`
                  }
                />
              ) : (
                <DynamicHeightComponent>
                  <Scrollbar>
                    {isMobile ? (
                      <SnapchatMobileViewPosts
                        publishedPosts={publishedPosts}
                        mobileLoader={mobileLoader}
                        getMobileViewNextPosts={getNextMobileSnapchatPublishedPosts}
                        pagination={pagination}
                      />
                    ) : (
                      <TableContainer sx={{
                  height: isShowOfferBanner ? 'calc(100vh - 380px)' : 'calc(100vh - 330px)',
              }}>
                        <Table stickyHeader size="small">
                          <UserListHead
                            headLabel={TABLE_HEAD}
                            rowCount={publishedPosts.length}
                            isShowCheckbox={false}
                          />
                          <TableBody>{getTableBodyData()}</TableBody>
                        </Table>
                      </TableContainer>
                    )}
                     {!isMobile && (
            <TablePagination
              component="div"
              rowsPerPageOptions={[10, 20, 30, 50]}
              count={publishedPosts?.length + 1}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              labelDisplayedRows={() => ''}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
                  </Scrollbar>
                </DynamicHeightComponent>
              )}
            </Card>
          </>
        ) : isLoading ? (
          <Spinner height={height} />
        ) : (
          <Card sx={{ height: height, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {hasIntegratationPermission ? (
              <LoadingButton variant="contained" onClick={() => navigate('/integration')}>
                Connect Snapchat Account
              </LoadingButton>
            ) : (
              <NoPostFound />
            )}
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default SnapchatPostGrid;